// ============================================================================
// Display Name Utility - Frontend helper for privacy-respecting display names
// ============================================================================
//
// Implements Spec 034: UI Transition from Name to Username
// Privacy Hierarchy: display_name > username > name
//
// NOTE: The backend computes displayName server-side and includes it in API
// responses. This utility provides fallback logic for edge cases where
// displayName might not be present (old cached data, etc.).
// ============================================================================

/**
 * User-like object with name fields
 */
interface UserNameFields {
  displayName?: string;
  username?: string;
  name?: string;
  // Legacy camelCase fields from API responses
  userName?: string;
}

/**
 * Get the privacy-respecting display name for a user.
 *
 * Priority:
 * 1. Pre-computed displayName from API (preferred - server handles hierarchy)
 * 2. username (global pseudonym)
 * 3. name (real name - fallback only)
 *
 * @param user - User object with name fields
 * @returns Display name string, or 'Unknown' if no name available
 */
export function getDisplayName(user: UserNameFields | null | undefined): string {
  if (!user) {
    return 'Unknown';
  }

  // Priority 1: Pre-computed displayName from API (backend handles full hierarchy)
  if (user.displayName) {
    return user.displayName;
  }

  // Priority 2: Global username/pseudonym
  if (user.username) {
    return user.username;
  }

  // Priority 3: Real name (legacy field)
  if (user.name) {
    return user.name;
  }

  // Priority 4: Legacy userName field (for backwards compatibility)
  if (user.userName) {
    return user.userName;
  }

  return 'Unknown';
}

/**
 * Get initials from a display name (for avatar fallbacks)
 *
 * @param name - Display name string
 * @returns 1-2 character initials
 */
export function getInitials(name: string | null | undefined): string {
  if (!name || name === 'Unknown') {
    return '?';
  }

  const parts = name.trim().split(/\s+/);

  if (parts.length === 1) {
    // Single word - return first 1-2 chars
    return parts[0].slice(0, 2).toUpperCase();
  }

  // Multiple words - return first letter of first and last
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Get display name with initials for compact display
 *
 * @param user - User object with name fields
 * @returns Object with displayName and initials
 */
export function getDisplayNameWithInitials(user: UserNameFields | null | undefined): {
  displayName: string;
  initials: string;
} {
  const displayName = getDisplayName(user);
  const initials = getInitials(displayName);

  return { displayName, initials };
}

/**
 * Format a user for display in a list or mention
 * Returns displayName with optional email hint for disambiguation
 *
 * @param user - User object with name and email fields
 * @param includeEmail - Whether to include email hint
 * @returns Formatted display string
 */
export function formatUserForDisplay(
  user: UserNameFields & { email?: string } | null | undefined,
  includeEmail: boolean = false
): string {
  if (!user) {
    return 'Unknown';
  }

  const displayName = getDisplayName(user);

  if (includeEmail && user.email) {
    return `${displayName} (${user.email})`;
  }

  return displayName;
}

/**
 * Check if a user has a custom display name set
 * (either org display_name or global username)
 *
 * @param user - User object
 * @returns true if user has a privacy-respecting identifier set
 */
export function hasCustomDisplayName(user: UserNameFields | null | undefined): boolean {
  if (!user) {
    return false;
  }

  // Has displayName computed by backend (which means display_name or username was set)
  // OR has explicit username
  return Boolean(user.displayName || user.username);
}

/**
 * Get a shortened display name for tight spaces (max 15 chars)
 *
 * @param user - User object with name fields
 * @param maxLength - Maximum length (default 15)
 * @returns Truncated display name with ellipsis if needed
 */
export function getShortDisplayName(
  user: UserNameFields | null | undefined,
  maxLength: number = 15
): string {
  const displayName = getDisplayName(user);

  if (displayName.length <= maxLength) {
    return displayName;
  }

  return displayName.slice(0, maxLength - 1) + '…';
}
